import { BlockRegistry } from '../voxel/BlockRegistry';

export class HUD {
  private container: HTMLDivElement;
  private crosshair: HTMLDivElement;
  private hotbarSlots: HTMLDivElement[] = [];
  private debugPanel: HTMLDivElement;
  private registry: BlockRegistry;

  private isDebugVisible: boolean = false;

  constructor() {
    this.registry = BlockRegistry.getInstance();
    this.container = this.createContainer();
    this.crosshair = this.createCrosshair();
    this.createHotbar();
    this.debugPanel = this.createDebugPanel();
    this.createClickToPlayMessage();

    document.body.appendChild(this.container);
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'game-hud';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 100;
      font-family: 'Segoe UI', Arial, sans-serif;
    `;
    return container;
  }

  private createCrosshair(): HTMLDivElement {
    const crosshair = document.createElement('div');
    crosshair.id = 'crosshair';
    crosshair.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 20px;
    `;

    // Horizontal line
    const hLine = document.createElement('div');
    hLine.style.cssText = `
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 2px;
      background: white;
      transform: translateY(-50%);
      box-shadow: 0 0 2px rgba(0,0,0,0.8);
    `;

    // Vertical line
    const vLine = document.createElement('div');
    vLine.style.cssText = `
      position: absolute;
      top: 0;
      left: 50%;
      width: 2px;
      height: 100%;
      background: white;
      transform: translateX(-50%);
      box-shadow: 0 0 2px rgba(0,0,0,0.8);
    `;

    crosshair.appendChild(hLine);
    crosshair.appendChild(vLine);
    this.container.appendChild(crosshair);

    return crosshair;
  }

  private createHotbar(): void {
    const hotbar = document.createElement('div');
    hotbar.id = 'hotbar';
    hotbar.style.cssText = `
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 4px;
      padding: 6px;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 8px;
      border: 2px solid rgba(255, 255, 255, 0.2);
    `;

    // Create 9 slots
    for (let i = 0; i < 9; i++) {
      const slot = document.createElement('div');
      slot.className = 'hotbar-slot';
      slot.dataset.slot = i.toString();
      slot.style.cssText = `
        width: 48px;
        height: 48px;
        background: rgba(0, 0, 0, 0.4);
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      `;

      // Block color preview
      const blockPreview = document.createElement('div');
      blockPreview.className = 'block-preview';
      blockPreview.style.cssText = `
        width: 32px;
        height: 32px;
        border-radius: 4px;
        box-shadow: inset 0 -2px 4px rgba(0,0,0,0.3);
      `;
      slot.appendChild(blockPreview);

      // Slot number
      const slotNum = document.createElement('span');
      slotNum.textContent = (i + 1).toString();
      slotNum.style.cssText = `
        position: absolute;
        top: 2px;
        left: 4px;
        font-size: 10px;
        color: rgba(255, 255, 255, 0.7);
        text-shadow: 1px 1px 1px rgba(0,0,0,0.5);
      `;
      slot.appendChild(slotNum);

      this.hotbarSlots.push(slot);
      hotbar.appendChild(slot);
    }

    this.container.appendChild(hotbar);
  }

  private createDebugPanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.id = 'debug-panel';
    panel.style.cssText = `
      position: absolute;
      top: 10px;
      left: 10px;
      padding: 10px 15px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 6px;
      color: white;
      font-size: 12px;
      font-family: 'Consolas', monospace;
      line-height: 1.6;
      display: none;
    `;
    this.container.appendChild(panel);
    return panel;
  }

  private createClickToPlayMessage(): HTMLDivElement {
    const message = document.createElement('div');
    message.id = 'click-to-play';
    message.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px 40px;
      background: rgba(0, 0, 0, 0.8);
      border-radius: 10px;
      color: white;
      font-size: 18px;
      text-align: center;
      pointer-events: none;
    `;
    message.innerHTML = `
      <div style="font-size: 24px; margin-bottom: 10px;">Controles</div>
      <div style="font-size: 14px; color: #aaa;">
        Botão direito + Mouse: Olhar ao redor<br>
        WASD: Mover | Espaço: Pular
      </div>
    `;
    // Hide by default - no click to play needed
    message.style.display = 'none';
    this.container.appendChild(message);
    return message;
  }

  public setPointerLocked(locked: boolean): void {
    // Now "locked" means right mouse button is held (camera rotation mode)
    if (locked) {
      this.crosshair.style.display = 'block';
    } else {
      this.crosshair.style.display = 'none';
    }
  }

  public updateHotbar(blocks: number[], selectedSlot: number): void {
    for (let i = 0; i < this.hotbarSlots.length; i++) {
      const slot = this.hotbarSlots[i];
      const preview = slot.querySelector('.block-preview') as HTMLDivElement;

      // Update block color
      const blockDef = this.registry.get(blocks[i]);
      if (blockDef && preview) {
        const color = blockDef.color;
        preview.style.background = `rgb(${Math.floor(color.r * 255)}, ${Math.floor(color.g * 255)}, ${Math.floor(color.b * 255)})`;
        preview.title = blockDef.displayName;
      }

      // Update selection
      if (i === selectedSlot) {
        slot.style.borderColor = '#ffcc00';
        slot.style.boxShadow = '0 0 10px rgba(255, 204, 0, 0.5)';
      } else {
        slot.style.borderColor = 'rgba(255, 255, 255, 0.3)';
        slot.style.boxShadow = 'none';
      }
    }
  }

  public updateDebug(info: {
    fps: number;
    position: { x: number; y: number; z: number };
    chunks: number;
    meshes: number;
    cameraMode: string;
    grounded: boolean;
    blockLooking?: string;
  }): void {
    if (!this.isDebugVisible) return;

    this.debugPanel.innerHTML = `
      <div><b>Dublox</b> - Debug (F3)</div>
      <div>FPS: ${info.fps.toFixed(1)}</div>
      <div>Pos: ${info.position.x.toFixed(1)}, ${info.position.y.toFixed(1)}, ${info.position.z.toFixed(1)}</div>
      <div>Chunks: ${info.chunks}</div>
      <div>Meshes: ${info.meshes}</div>
      <div>Camera: ${info.cameraMode}</div>
      <div>Grounded: ${info.grounded ? 'Yes' : 'No'}</div>
      ${info.blockLooking ? `<div>Looking at: ${info.blockLooking}</div>` : ''}
    `;
  }

  public toggleDebug(): void {
    this.isDebugVisible = !this.isDebugVisible;
    this.debugPanel.style.display = this.isDebugVisible ? 'block' : 'none';
  }

  public showDebug(): void {
    this.isDebugVisible = true;
    this.debugPanel.style.display = 'block';
  }

  public hideDebug(): void {
    this.isDebugVisible = false;
    this.debugPanel.style.display = 'none';
  }

  public isDebugShowing(): boolean {
    return this.isDebugVisible;
  }

  public showCrosshair(): void {
    this.crosshair.style.display = 'block';
  }

  public hideCrosshair(): void {
    this.crosshair.style.display = 'none';
  }

  public dispose(): void {
    this.container.remove();
  }
}
